import { useMemo, useState } from "react";
import { MagnifyingGlass, Plant as PlantIcon, Check, ClockCounterClockwise } from "@phosphor-icons/react";
import { Input } from "../ui/input";
import { fileUrl } from "../../lib/api";
import { getRecent, pushRecent } from "../../lib/recent";

export const PlantThumb = ({ plant, className = "" }) => (
  <span className={`relative block overflow-hidden ${className}`}>
    {plant?.photo_path ? <img loading="lazy" decoding="async" src={fileUrl(plant.photo_path, 160)} alt="" className="h-full w-full object-cover" />
      : <span className="h-full w-full flex items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5"><PlantIcon weight="duotone" className="h-1/3 w-1/3 text-primary/70" /></span>}
  </span>
);

const chip = (on) => `shrink-0 h-8 px-3 rounded-full text-xs font-medium transition-colors ${on ? "bg-foreground text-background" : "bg-muted/70 text-muted-foreground hover:text-foreground"}`;

function Tile({ p, on, onPick, prefix }) {
  return (
    <button type="button" onClick={() => onPick(p.id)} data-testid={`${prefix}-option-${p.id}`} aria-pressed={on}
      className={`group text-left rounded-2xl p-1.5 transition-[background-color,box-shadow,transform] active:scale-[0.97] ${on ? "bg-foreground/[0.06] ring-2 ring-foreground" : "hover:bg-muted/60"}`}>
      <span className="relative block">
        <PlantThumb plant={p} className="aspect-square rounded-xl" />
        {on && <span className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-foreground text-background flex items-center justify-center"><Check weight="bold" className="h-3.5 w-3.5" /></span>}
        {p.critical_active && <span className="absolute bottom-1.5 left-1.5 h-2.5 w-2.5 rounded-full bg-[#FF3B30] ring-2 ring-background" />}
      </span>
      <span className="block mt-1.5 px-0.5 text-xs font-medium line-clamp-2 break-words">{p.nickname}</span>
      {p.species && <span className="block px-0.5 text-[10px] text-muted-foreground truncate">{p.species}</span>}
      <span className="block px-0.5 font-mono text-[9px] leading-tight tracking-tight text-muted-foreground/80 whitespace-nowrap overflow-hidden text-ellipsis" title={p.plant_code} data-testid={`${prefix}-code-${p.id}`}>{p.plant_code}</span>
    </button>
  );
}

export default function PlantGrid({ plants, value, onPick, prefix = "critical-plant" }) {
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const recentIds = useMemo(() => getRecent("plants"), []);
  const locations = useMemo(() => [...new Set(plants.map((p) => p.location).filter(Boolean))].sort(), [plants]);
  const pick = (id) => { pushRecent("plants", id); onPick(id); };
  const shown = plants.filter((p) => (!loc || p.location === loc) && `${p.nickname} ${p.species || ""} ${p.scientific_name || ""} ${p.plant_code} ${p.location || ""}`.toLowerCase().includes(q.toLowerCase()));
  const recent = !q && !loc ? recentIds.map((id) => plants.find((p) => p.id === id)).filter(Boolean).slice(0, 4) : [];
  const rest = shown.filter((p) => !recent.includes(p));
  return (
    <>
      <div className="relative">
        <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid={`${prefix}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, species or ID"
          onKeyDown={(e) => { if (e.key === "Enter" && shown[0]) { e.preventDefault(); pick(shown[0].id); } }}
          className="pl-10 rounded-xl h-11 bg-muted/50 border-0" />
      </div>
      {locations.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1" data-testid={`${prefix}-locations`}>
          <button type="button" className={chip(!loc)} onClick={() => setLoc("")}>All</button>
          {locations.map((l) => <button key={l} type="button" className={chip(loc === l)} onClick={() => setLoc(loc === l ? "" : l)} data-testid={`${prefix}-loc-${l}`}>{l}</button>)}
        </div>
      )}
      {recent.length > 0 && (
        <>
          <p className="mt-4 text-[11px] uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5"><ClockCounterClockwise className="h-3 w-3" />Recent</p>
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid={`${prefix}-recent`}>
            {recent.map((p) => <Tile key={p.id} p={p} on={p.id === value} onPick={pick} prefix={prefix} />)}
          </div>
          <p className="mt-5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">All plants</p>
        </>
      )}
      <div className={`${recent.length ? "mt-2" : "mt-4"} grid grid-cols-3 sm:grid-cols-4 gap-3`} data-testid={`${prefix}-grid`}>
        {rest.map((p) => <Tile key={p.id} p={p} on={p.id === value} onPick={pick} prefix={prefix} />)}
        {!shown.length && <p className="col-span-full py-8 text-center text-xs text-muted-foreground">No plants match “{q || loc}”.</p>}
      </div>
    </>
  );
}
